import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, TextField, Divider } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import SingleSelect from 'components/common/SingleSelect';
import { useNotificationContext } from 'contexts/NotificationContext';
import salesforceFundraisingsService from 'services/salesforceFundraisingsService';

const FundraisingField = ({ label, children, value }) => (
	<Box sx={{ mb: 2 }}>
		<Typography
			sx={{
				fontSize: '11px',
				fontWeight: 600,
				textTransform: 'uppercase',
				color: 'text.tertiary',
				mb: '4px',
			}}
		>
			{label}
		</Typography>

		{children ?? (
			<Typography
				sx={{
					fontSize: '13px',
					fontWeight: 500,
					color: 'text.primary',
				}}
			>
				{value || '—'}
			</Typography>
		)}
	</Box>
);

FundraisingField.propTypes = {
	label: PropTypes.string.isRequired,
	children: PropTypes.node,
	value: PropTypes.node,
};

const FundraisingDrawer = ({ open, fundraising, onClose, onSaved }) => {
	const { showNotification } = useNotificationContext();

	const [saving, setSaving] = useState(false);

	// Editable field state
	const [commitment, setCommitment] = useState('');
	const [stage, setStage] = useState('');
	const [anticipatedCloseDate, setAnticipatedCloseDate] = useState(null);
	const [closingRound, setClosingRound] = useState('');

	// Picklist options
	const [picklistValues, setPicklistValues] = useState(null);
	const [picklistLoading, setPicklistLoading] = useState(false);
	const picklistFetched = useRef(false);

	// Initialize form state from fundraising data
	const initializeFormState = (data) => {
		if (!data) return;
		setCommitment(data.commitment ?? '');
		setStage(data.stage ?? '');
		setAnticipatedCloseDate(
			data.anticipated_close_date ? dayjs(data.anticipated_close_date) : null
		);
		setClosingRound(data.closing_round ?? '');
	};

	useEffect(() => {
		initializeFormState(fundraising);
	}, [fundraising]);

	// Fetch picklist values once when opening the drawer
	useEffect(() => {
		if (!open || picklistFetched.current) return;

		const fetchPicklists = async () => {
			setPicklistLoading(true);
			try {
				const data = await salesforceFundraisingsService.getPicklistValues();
				setPicklistValues(data);
				picklistFetched.current = true;
			} catch (err) {
				console.error('Failed to load picklist values:', err);
			} finally {
				setPicklistLoading(false);
			}
		};

		fetchPicklists();
	}, [open]);

	// Build a payload of only changed fields
	const buildPayload = () => {
		if (!fundraising) return {};

		const payload = {};

		const commitmentNum = commitment !== '' ? Number(commitment) : null;
		const originalCommitment = fundraising.commitment ?? null;
		if (commitmentNum !== originalCommitment) {
			payload.commitment = commitmentNum;
		}

		if (stage !== (fundraising.stage ?? '')) {
			payload.stage = stage;
		}

		const formattedDate = anticipatedCloseDate?.isValid()
			? anticipatedCloseDate.format('YYYY-MM-DD')
			: null;
		const originalDate = fundraising.anticipated_close_date ?? null;
		if (formattedDate !== originalDate) {
			payload.anticipated_close_date = formattedDate;
		}

		if (closingRound !== (fundraising.closing_round ?? '')) {
			payload.closing_round = closingRound;
		}

		return payload;
	};

	const handleSave = async () => {
		if (!fundraising) return;

		const payload = buildPayload();
		if (Object.keys(payload).length === 0) {
			showNotification('No changes to save', 'info');
			return;
		}

		setSaving(true);
		try {
			await salesforceFundraisingsService.updateFundraising(
				fundraising.id,
				payload
			);

			showNotification('Fundraising opportunity updated', 'success');
			onSaved?.();
			onClose();
		} catch (err) {
			showNotification(
				err?.message || 'Failed to update fundraising opportunity',
				'error'
			);
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		initializeFormState(fundraising);
		onClose();
	};

	return (
		<>
			{/* backdrop */}
			<Box
				onClick={handleCancel}
				sx={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.25)',
					opacity: open ? 1 : 0,
					pointerEvents: open ? 'auto' : 'none',
					transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
					zIndex: 1200,
				}}
			/>

			{/* panel */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: 480,
					backgroundColor: 'background.paper',
					transform: open ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1)',
					zIndex: 1201,
					display: 'flex',
					flexDirection: 'column',
					boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
				}}
			>
				{/* header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						padding: '20px 24px',
						borderBottom: '1px solid',
						borderColor: 'border.light',
						backgroundColor: 'background.subtle',
					}}
				>
					<Box>
						<Typography
							sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.3 }}
						>
							Edit Fundraising Opportunity
						</Typography>
						{fundraising?.name && (
							<Typography
								sx={{
									fontSize: '14px',
									color: 'text.secondary',
									mt: '4px',
								}}
							>
								{fundraising.name}
							</Typography>
						)}
					</Box>

					<IconButton
						onClick={handleCancel}
						sx={{
							mt: -0.5,
							mr: -1,
							color: 'text.tertiary',
							'&:hover': { backgroundColor: 'background.subtle' },
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* CRM info blurb */}
				{fundraising && (
					<Box
						sx={{
							mx: 5,
							mt: 3,
							mb: 0,
							px: 2,
							py: 1.5,
							borderRadius: 1,
							backgroundColor: 'status.blue.bg',
							border: '1px solid',
							borderColor: 'status.blue.border',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<InfoIcon fontSize='small' sx={{ color: 'status.blue.icon' }} />
							<Typography sx={{ fontSize: '12px', color: 'status.blue.text' }}>
								Changes made within this modal will flow back into the Altvia CRM.
							</Typography>
						</Box>
					</Box>
				)}

				{/* body */}
				{fundraising && (
					<Box sx={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
						{/* Read-only fields */}
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: '16px',
								mb: 3,
							}}
						>
							<FundraisingField
								label='Investor Type'
								value={fundraising.investor_type}
							/>
							<FundraisingField
								label='Existing Investor'
								value={fundraising.existing_investor ? 'Yes' : 'No'}
							/>
						</Box>

						<Divider sx={{ mb: 3 }} />

						{/* Editable fields */}
						<FundraisingField label='Commitment'>
							<TextField
								type='number'
								size='small'
								fullWidth
								value={commitment}
								onChange={(e) => setCommitment(e.target.value)}
								disabled={saving}
								placeholder='Enter commitment amount'
							/>
						</FundraisingField>

						<FundraisingField label='Stage'>
							<SingleSelect
								value={stage}
								options={picklistValues?.stage ?? []}
								loading={picklistLoading}
								disabled={saving}
								placeholder='Select a stage'
								onChange={(val) => setStage(val)}
							/>
						</FundraisingField>

						<FundraisingField label='Anticipated Close Date'>
							<DatePicker
								value={anticipatedCloseDate}
								onChange={(val) => setAnticipatedCloseDate(val)}
								disabled={saving}
								slotProps={{
									textField: {
										size: 'small',
										fullWidth: true,
									},
								}}
							/>
						</FundraisingField>

						<FundraisingField label='Closing Round'>
							<SingleSelect
								value={closingRound}
								options={picklistValues?.closing_round ?? []}
								loading={picklistLoading}
								disabled={saving}
								placeholder='Select a closing round'
								onChange={(val) => setClosingRound(val)}
							/>
						</FundraisingField>
					</Box>
				)}

				{/* footer */}
				{fundraising && (
					<Box
						sx={{
							padding: '16px 24px',
							borderTop: '1px solid',
							borderColor: 'border.light',
							display: 'flex',
							justifyContent: 'flex-end',
							gap: 1,
						}}
					>
						<LoadingButton
							variant='text'
							size='small'
							disabled={saving}
							onClick={handleCancel}
						>
							Cancel
						</LoadingButton>
						<LoadingButton
							variant='contained'
							size='small'
							loading={saving}
							disabled={saving}
							onClick={handleSave}
						>
							Save Changes
						</LoadingButton>
					</Box>
				)}
			</Box>
		</>
	);
};

FundraisingDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	fundraising: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func,
};

export default FundraisingDrawer;
